import clsx from 'clsx'
import { Icon } from '@iconify/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Dropdown } from '@pepper/ui'
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { subMonths, format } from 'date-fns'

import { useRoiIntervals } from 'container/SEOTools/hooks/useRoiIntervals'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { QueryParamFilters } from '../../types'
import { IntervalsData, RoiInterval } from 'types/roi_intervals'

import styles from './DateRangeDropdown.module.css'

const PREVIOUS_MONTH = 'Previous Month'
const PREVIOUS_WEEK = 'Previous Week'

const DateRangeDropdown = () => {
  const companyId = useSelectedCompanyId()
  const previousMonthWithYear = format(subMonths(new Date(), 1), 'MMM yyyy')
  const [openDate, setOpenDate] = useState(false)
  const [selectedRange, setSelectedRange] =
    useState<keyof IntervalsData['data']>(PREVIOUS_MONTH)
  const [selectedValue, setSelectedValue] = useState<string>(
    previousMonthWithYear
  )

  const periodMapping = useMemo(
    () => ({
      'Previous Month': 'monthly',
      'Previous Week': 'weekly',
      monthly: 'monthly',
      weekly: 'weekly',
      quarterly: 'quarterly',
    }),
    []
  )

  const mapPeriod = useCallback(
    (key: keyof IntervalsData['data']) => periodMapping[key] ?? key,
    [periodMapping]
  )

  const [_, setQueryParams] = useQueryParams<QueryParamFilters>({
    period: withDefault(StringParam, undefined),
    startDate: withDefault(StringParam, undefined),
    page: withDefault(NumberParam, 1),
  })

  const { data: intervalsData } = useRoiIntervals({ companyId })

  const allRanges = intervalsData?.data
    ? (Object.keys(intervalsData.data) as (keyof IntervalsData['data'])[])
    : []

  const selectedIntervals = intervalsData?.data[selectedRange] ?? []

  const getIntervalData = (
    range: keyof IntervalsData['data']
  ): RoiInterval | null => {
    const interval = intervalsData?.data?.[range]

    if (!interval) return null

    return Array.isArray(interval) ? interval[0] : interval
  }

  useEffect(() => {
    if (!intervalsData?.data) return

    const firstValidEntry = Object.entries(intervalsData.data).find(
      ([, interval]) =>
        Array.isArray(interval) ? interval.length > 0 : !!interval
    )

    if (!firstValidEntry) return

    const [firstKey, firstIntervalData] = firstValidEntry as [
      keyof IntervalsData['data'],
      RoiInterval | RoiInterval[]
    ]

    if (!firstIntervalData) return

    const firstInterval = Array.isArray(firstIntervalData)
      ? firstIntervalData[0]
      : firstIntervalData

    if (!firstInterval) return

    setSelectedRange(firstKey)
    setSelectedValue(firstInterval.display_date)

    setQueryParams({
      period: mapPeriod(firstKey),
      startDate: firstInterval.start_date,
      page: 1,
      insights: [PREVIOUS_MONTH, PREVIOUS_WEEK].includes(firstKey)
        ? true
        : undefined,
    })
  }, [intervalsData, mapPeriod, setQueryParams])

  const handleChange = (range: keyof IntervalsData['data'], value: string) => {
    setQueryParams({
      period: mapPeriod(range),
      startDate: value,
      page: 1,
      insights:
        range === PREVIOUS_WEEK || range === PREVIOUS_MONTH ? true : undefined,
    })
  }

  const handleRangeClick = (range: keyof IntervalsData['data']) => {
    const interval = getIntervalData(range)

    if (!interval) return

    const { start_date, display_date } = interval

    setSelectedRange(range)
    setSelectedValue(display_date)
    setOpenDate(true)
    setQueryParams({
      period: mapPeriod(range),
      startDate: start_date,
      page: 1,
      insights: [PREVIOUS_WEEK, PREVIOUS_MONTH].includes(range)
        ? true
        : undefined,
    })

    handleChange(range, start_date)
  }

  const rangeMenu = (
    <div className={styles.menu}>
      {allRanges.slice(0, 2).map(range => (
        <Button
          key={range}
          type="text"
          className={clsx(styles.menuItem, {
            [styles.active]: selectedRange === range,
          })}
          onClick={() => handleRangeClick(range)}
        >
          {range}
        </Button>
      ))}
      <div className={styles.divider} />
      {allRanges.slice(2).map(range => (
        <Button
          key={range}
          type="text"
          className={clsx(styles.menuItem, {
            [styles.active]: selectedRange === range,
          })}
          onClick={() => handleRangeClick(range)}
        >
          {range}
        </Button>
      ))}
    </div>
  )

  const valuesMenu = (
    <div className={styles.menu}>
      {(Array.isArray(selectedIntervals)
        ? selectedIntervals
        : [selectedIntervals]
      ).map(value => (
        <Button
          key={value.display_date}
          type="text"
          className={clsx(styles.menuItem, {
            [styles.active]: selectedValue === value.display_date,
          })}
          onClick={() => {
            setSelectedValue(value.display_date)
            setOpenDate(false)
            handleChange(selectedRange, value.start_date)
          }}
        >
          {value.display_date}
        </Button>
      ))}
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <Dropdown overlay={rangeMenu} trigger={['click']} placement="bottomRight">
        <Button
          className={styles.leftBtn}
          icon={<Icon icon="lucide:calendar-range" width={16} height={16} />}
          iconPlacement="left"
        >
          {selectedRange}
          <Icon icon="lucide:chevron-down" width={18} height={18} />
        </Button>
      </Dropdown>
      <Dropdown
        overlay={valuesMenu}
        trigger={['click']}
        visible={openDate}
        onVisibleChange={setOpenDate}
        placement="bottomRight"
      >
        <Button className={styles.rightBtn}>
          {selectedValue}
          <Icon icon="lucide:chevron-down" width={18} height={18} />
        </Button>
      </Dropdown>
    </div>
  )
}

export default DateRangeDropdown
